import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import Input from '@/components/ui/Input';
import CheckboxMultiSelect from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import Button from '@/components/ui/Button';
import { useGetAuthApi, usePostAuthApi, usePutAuthApi, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { KollelDtoIn, KollelFormPayload, KollelStipendSettingsDtoIn, UpdateKollelAvrechsDtoIn } from '@/models/in/KollelDtoIn';
import SupportedDtoIn from '@/models/in/SupportedDtoIn';
import { StudyTimeOptions } from '@/constants/studyTimeOptions';
import KollelTransferConfirmDialog from '@/pages/newKollel/steps/KollelTransferConfirmDialog';

interface KollelFormDialogProps {
  fundId?: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Present when editing an existing kollel; absent when creating a new one. */
  kollel?: KollelDtoIn | null;
}

const emptyContact = { name: '', email: '', phone: '' };

const toFormState = (kollel?: KollelDtoIn | null): KollelFormPayload & { avrechIds: number[] } => ({
  name: kollel?.name ?? '',
  address: kollel?.address ?? '',
  studyTime: kollel?.studyTime ?? StudyTimeOptions.Morning,
  roshKollelContact: kollel?.roshKollelContact ?? { ...emptyContact },
  supervisingBodyContact: kollel?.supervisingBodyContact ?? { ...emptyContact },
  notes: kollel?.notes ?? '',
  excellenceLimit: kollel?.excellenceLimit ?? null,
  examsLimit: kollel?.examsLimit ?? null,
  punctualityLimit: kollel?.punctualityLimit ?? null,
  avrechIds: kollel?.avrechs?.map((a) => a.supportedId) ?? [],
});

/**
 * Create/edit form of the "כוללים" module (HAT-1446, phase 2). Deliberately a single-screen form,
 * not the full multi-step wizard from the Figma spec (that is phase 3) - name, address, study
 * time, the two contacts, notes, and a stopgap avrech picker (a searchable checkbox list of the
 * fund's own supporteds) in one submission.
 */
const KollelFormDialog = ({ fundId, open, onClose, onSaved, kollel }: KollelFormDialogProps) => {
  const { t } = useTranslation();
  const isEdit = !!kollel;
  const [form, setForm] = useState(() => toFormState(kollel));
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // HAT-1446 phase 7: supportedIds the coordinator confirmed a "בקשה להעברת כולל" for, so the save
  // below marks their row confirmTransfer - cleared with the rest of the form on open/close.
  const [transferConfirmedIds, setTransferConfirmedIds] = useState<Set<number>>(new Set());
  const [pendingTransfer, setPendingTransfer] = useState<{ supportedId: number; kollelId: number; kollelName: string } | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toFormState(kollel));
      setTransferConfirmedIds(new Set());
      setError(null);
    }
  }, [open, kollel]);

  const { data: supporteds } = useGetAuthApi<SupportedDtoIn[]>(fundId && open ? `funds/${fundId}/supporteds` : '');
  const { data: allKollels } = useGetAuthApi<KollelDtoIn[]>(fundId && open ? `funds/${fundId}/kollels` : '');
  const { data: stipendSettings } = useGetAuthApi<KollelStipendSettingsDtoIn>(
    fundId && open ? `funds/${fundId}/kollels/stipend-settings` : '',
  );
  const maxTotalStipend = stipendSettings?.maxTotalStipendPerAvrech ?? null;
  const stipendTotal = (form.excellenceLimit ?? 0) + (form.examsLimit ?? 0) + (form.punctualityLimit ?? 0);

  /** supportedId -> the OTHER kollel they are currently registered in, if any (HAT-1446 phase 7). */
  const elsewhereById = useMemo(() => {
    const map = new Map<number, { kollelId: number; kollelName: string }>();
    (allKollels ?? [])
      .filter((k) => k.id !== kollel?.id)
      .forEach((k) => k.avrechs.forEach((a) => map.set(a.supportedId, { kollelId: k.id, kollelName: k.name })));
    return map;
  }, [allKollels, kollel]);

  const avrechOptions = useMemo(
    () =>
      (supporteds ?? []).map((s) => {
        const elsewhere = elsewhereById.get(s.id);
        return {
          value: s.id,
          label: `${s.firstName} ${s.lastName}`.trim(),
          sublabel: elsewhere ? t('kollels.wizard.step2.registeredElsewhere', { kollel: elsewhere.kollelName }) : s.israeliId,
        };
      }),
    [supporteds, elsewhereById, t],
  );

  const handleAvrechIdsChange = (ids: number[]) => {
    const addedId = ids.find((id) => !form.avrechIds.includes(id));
    const elsewhere = addedId !== undefined ? elsewhereById.get(addedId) : undefined;
    if (addedId !== undefined && elsewhere) {
      setPendingTransfer({ supportedId: addedId, ...elsewhere });
      return; // Held back until the coordinator approves/rejects the transfer dialog.
    }
    setForm((prev) => ({ ...prev, avrechIds: ids }));
  };

  const createMutation = usePostAuthApi<KollelDtoIn, KollelFormPayload>(fundId ? `funds/${fundId}/kollels` : '');
  const updateMutation = usePutAuthApi<KollelDtoIn, undefined, KollelFormPayload>(
    fundId && kollel ? `funds/${fundId}/kollels/${kollel.id}` : '',
  );
  const updateAvrechsMutation = usePutAuthApi<KollelDtoIn, undefined, UpdateKollelAvrechsDtoIn>(
    fundId && kollel ? `funds/${fundId}/kollels/${kollel.id}/avrechs` : '',
  );
  const deleteMutation = useDeleteAuthApi<void>(fundId && kollel ? `funds/${fundId}/kollels/${kollel.id}` : '');

  const isSaving = createMutation.isPending || updateMutation.isPending || updateAvrechsMutation.isPending;

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(t('kollels.form.nameRequired'));
      return;
    }
    setError(null);

    const payload: KollelFormPayload = {
      name: form.name.trim(),
      address: form.address.trim(),
      studyTime: form.studyTime,
      roshKollelContact: form.roshKollelContact,
      supervisingBodyContact: form.supervisingBodyContact,
      notes: form.notes,
      excellenceLimit: form.excellenceLimit,
      examsLimit: form.examsLimit,
      punctualityLimit: form.punctualityLimit,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ body: payload });
        // This simple form has no per-avrech schedule UI (that's the wizard's step 3), so an
        // existing override must be preserved rather than silently wiped on every save - look it
        // up from the kollel we opened with, defaulting newly-added avrechs to null (the kollel's
        // own studyTime).
        const existingOverrideById = new Map(kollel!.avrechs.map((a) => [a.supportedId, a.studyTimeOverride]));
        const avrechsBody: UpdateKollelAvrechsDtoIn = {
          avrechs: form.avrechIds.map((supportedId) => ({
            supportedId,
            studyTimeOverride: existingOverrideById.get(supportedId) ?? null,
            confirmTransfer: transferConfirmedIds.has(supportedId),
          })),
        };
        await updateAvrechsMutation.mutateAsync({ body: avrechsBody });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSaved();
      onClose();
    } catch {
      setError(t('kollels.form.errorSaving'));
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        onSaved();
        onClose();
      },
      onError: () => {
        setConfirmDeleteOpen(false);
        setError(t('kollels.form.errorDeleting'));
      },
    });
  };

  const updateContact = (
    field: 'roshKollelContact' | 'supervisingBodyContact',
    subField: 'name' | 'email' | 'phone',
    value: string,
  ) => {
    setForm((prev) => ({ ...prev, [field]: { ...prev[field], [subField]: value } }));
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()} title={isEdit ? t('kollels.form.editTitle') : t('kollels.form.createTitle')}>
      <div className="flex flex-col gap-5 p-4 max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1">
            <span className="body-sm-semibold">{t('kollels.form.nameLabel')}</span>
            <Input
              value={form.name}
              placeholder={t('kollels.form.namePlaceholder')}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="body-sm-semibold">{t('kollels.form.addressLabel')}</span>
            <Input
              value={form.address}
              placeholder={t('kollels.form.addressPlaceholder')}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1">
          <span className="body-sm-semibold">{t('kollels.form.studyTimeLabel')}</span>
          <select
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={form.studyTime}
            onChange={(e) => setForm((prev) => ({ ...prev, studyTime: e.target.value as StudyTimeOptions }))}
          >
            {Object.values(StudyTimeOptions).map((option) => (
              <option key={option} value={option}>
                {t(`StudyTimeOptions.${option}`)}
              </option>
            ))}
          </select>
        </label>

        {(
          [
            ['roshKollelContact', 'roshKollelTitle'],
            ['supervisingBodyContact', 'supervisingBodyTitle'],
          ] as const
        ).map(([field, titleKey]) => (
          <div key={field} className="flex flex-col gap-2 border border-(--border-main) rounded-lg p-3">
            <span className="body-sm-semibold">{t(`kollels.form.${titleKey}`)}</span>
            <div className="grid grid-cols-3 gap-3">
              <label className="flex flex-col gap-1">
                <span className="body-xs-regular text-(--font-secondary)">{t('kollels.form.contactNameLabel')}</span>
                <Input
                  value={form[field].name}
                  placeholder={t('kollels.form.contactNamePlaceholder')}
                  onChange={(e) => updateContact(field, 'name', e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="body-xs-regular text-(--font-secondary)">{t('kollels.form.emailLabel')}</span>
                <Input
                  type="email"
                  value={form[field].email}
                  onChange={(e) => updateContact(field, 'email', e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="body-xs-regular text-(--font-secondary)">{t('kollels.form.phoneLabel')}</span>
                <Input
                  type="tel"
                  value={form[field].phone}
                  onChange={(e) => updateContact(field, 'phone', e.target.value)}
                />
              </label>
            </div>
          </div>
        ))}

        {/* HAT-1446 phase 4: the kollel's own per-avrech stipend sub-caps. The total ceiling is
         * only shown/enforced once the office approves a real figure (KollelSettings) - until
         * then the three fields are free, and the helper text explains why there is no limit. */}
        <fieldset className="flex flex-col gap-2 border border-(--border-main) rounded-lg p-3">
          <legend className="body-sm-semibold px-1">{t('kollels.form.stipendTitle')}</legend>
          <div className="grid grid-cols-3 gap-3">
            {(
              [
                ['excellenceLimit', 'excellenceLimitLabel'] as const,
                ['examsLimit', 'examsLimitLabel'] as const,
                ['punctualityLimit', 'punctualityLimitLabel'] as const,
              ]
            ).map(([field, labelKey]) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="body-xs-regular text-(--font-secondary)">{t(`kollels.form.${labelKey}`)}</span>
                <Input
                  type="number"
                  min={0}
                  value={form[field] ?? ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value === '' ? null : Number(e.target.value) }))}
                />
              </label>
            ))}
          </div>
          <p className="body-xs-regular text-(--font-secondary)">
            {maxTotalStipend != null
              ? t('kollels.form.stipendTotalWithCap', { total: stipendTotal, max: maxTotalStipend })
              : t('kollels.form.stipendTotalNoCap', { total: stipendTotal })}
          </p>
        </fieldset>

        {/*
         * Stopgap avrech picker (HAT-1446 phase 2): a searchable checkbox list of the fund's own
         * supporteds, reusing CheckboxMultiSelect as-is. The Figma spec's filtered picker with
         * inline add-supported rows is phase 3; this only exists on edit, since the kollel must
         * exist first (avrechs are reconciled against its id).
         */}
        {isEdit && (
          <label className="flex flex-col gap-1">
            <span className="body-sm-semibold">{t('kollels.form.avrechsLabel')}</span>
            <CheckboxMultiSelect
              options={avrechOptions}
              selected={form.avrechIds}
              onChange={handleAvrechIdsChange}
              emptyText={t('kollels.form.avrechsEmptyText')}
            />
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="body-sm-semibold">{t('kollels.form.notesLabel')}</span>
          <textarea
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular min-h-[72px]"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </label>

        {error && <p className="body-sm-regular text-(--color-danger)">{error}</p>}
      </div>

      <div className="flex items-center justify-between p-4 border-t border-(--border-main)">
        {isEdit ? (
          <Button variant="outline" onClick={() => setConfirmDeleteOpen(true)}>
            {t('kollels.form.delete')}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('kollels.form.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('kollels.form.saving') : t('kollels.form.save')}
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t('kollels.form.deleteConfirmTitle')}
        message={t('kollels.form.deleteConfirmMessage', { name: kollel?.name })}
        confirmText="kollels.form.delete"
        cancelText="kollels.form.cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
        isLoading={deleteMutation.isPending}
        variant="danger"
      />

      {pendingTransfer && (
        <KollelTransferConfirmDialog
          open
          avrechName={
            `${(supporteds ?? []).find((s) => s.id === pendingTransfer.supportedId)?.firstName ?? ''} ${(supporteds ?? []).find((s) => s.id === pendingTransfer.supportedId)?.lastName ?? ''}`.trim()
          }
          fromKollelName={pendingTransfer.kollelName}
          toKollelName={form.name}
          onApprove={() => {
            setTransferConfirmedIds((prev) => new Set(prev).add(pendingTransfer.supportedId));
            setForm((prev) => ({ ...prev, avrechIds: [...prev.avrechIds, pendingTransfer.supportedId] }));
            setPendingTransfer(null);
          }}
          onReject={() => setPendingTransfer(null)}
        />
      )}
    </MainDialogLayout>
  );
};

export default KollelFormDialog;
